import {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {TFunction} from 'i18next';
import {IS_LEADER_GUEST_STORAGE_KEY} from 'utils/constants';
import {PATHS} from 'routes/constants';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {SessionStorage} from '@chekin/core';
import {appendLeaderParam} from 'utils/guestForm';
import {useGuestGroup, usePath} from 'hooks';
import {StepInfoBox} from 'pages/Auth/components/StepInfoBox';
import {BoxInfo, NavigationStatus} from '@guestapp/old-ui-kit';
import MultiRadio from 'components/common/MultiRadio';
import {Wrapper, Container, StyledButton} from './styled';
import {RadiosContent} from 'styled/common';

const getOptions = (t: TFunction) => {
  return [
    {label: t('guest_in_charge_reservation'), value: 'true'},
    {label: t('accompanying_guest'), value: 'false'},
  ];
};

function WhoIsRegistering() {
  const {t} = useTranslation();
  const [option, setOption] = useState(() => {
    const storedValue = SessionStorage.get(IS_LEADER_GUEST_STORAGE_KEY);
    const foundOption = getOptions(t).find(opt => opt.value === storedValue);
    return foundOption || getOptions(t)[0];
  });

  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {
    identityVerificationDetails: {enabled: ivEnabled},
  } = useSchema();
  const {guestGroup} = useGuestGroup();
  const {hasAvantio} = useReservation();
  const hasNoGuests = guestGroup?.added_number_of_guests === 0;

  useEffect(() => {
    if (option.value) {
      SessionStorage.set(IS_LEADER_GUEST_STORAGE_KEY, option.value);
    }
  }, [option.value]);

  const handleConfirm = useCallback(() => {
    const isLeader = option.value === 'true';
    let nextPath = '';
    if (hasAvantio) {
      nextPath = PATHS.guestRegistration;
    }
    if (ivEnabled) {
      if (hasNoGuests) {
        nextPath = PATHS.verification.main;
      } else {
        nextPath = PATHS.guestRegistration;
      }
    } else {
      if (hasNoGuests) {
        nextPath = PATHS.addPersonalDataForm;
      } else {
        nextPath = PATHS.guestRegistration;
      }
    }
    navigate(appendLeaderParam(getFullPath(nextPath), isLeader));
  }, [navigate, getFullPath, hasAvantio, ivEnabled, hasNoGuests, option.value]);

  return (
    <Wrapper>
      <Container>
        <StepInfoBox
          title={t('who_are_you_registering')}
          description={t('select_the_the_person_in_charge_of_this_reservation')}
        />
        <RadiosContent>
          <MultiRadio options={getOptions(t)} value={option} onChange={setOption} />
        </RadiosContent>
        <BoxInfo
          type={NavigationStatus.information}
          text={t('the_guest_in_charge_must_be_an_adult')}
        />
        <StyledButton onClick={handleConfirm} variant="primary">
          {t('confirm')}
        </StyledButton>
      </Container>
    </Wrapper>
  );
}

export {WhoIsRegistering};
